import { Namespace, Ref, SequencerConfigState } from '@repo/sdk/models/components';

import { extractConditionExpressionRefs } from '@/features/equipment-browser/conditional-evaluator/extract-refs';

/**
 * Extracts the reading references needed to evaluate conditions for deciding which sequencer is active.
 *
 * @param sequencerConfigStates - The sequencer configurations to extract the references from
 * @returns The reading references used to evaluate the sequencer conditions.
 */
export const extractReadingsRefsFromSequencerConfigStates = (
  sequencerConfigStates: SequencerConfigState[] = []
): Ref[] => {
  if (!sequencerConfigStates || sequencerConfigStates.length === 0) {
    return [];
  }

  const refs: Ref[] = [];

  sequencerConfigStates.forEach((config: SequencerConfigState) => {
    if (config.conditionExpression) {
      refs.push(...extractConditionExpressionRefs(config.conditionExpression));
    }
  });

  return [
    ...new Set(
      refs.filter((ref): ref is Ref => ref !== undefined && ref.namespace !== Namespace.Setting)
    ),
  ];
};

/**
 * Extracts the setting references needed to evaluate conditions for deciding which sequencer is active.
 *
 * @param sequencerConfigStates - The sequencer configurations to extract the references from
 * @returns The setting references used to evaluate the sequencer conditions.
 */
export const extractSettingsRefsFromSequencerConfigStates = (
  sequencerConfigStates: SequencerConfigState[] = []
): Ref[] => {
  if (!sequencerConfigStates || sequencerConfigStates.length === 0) {
    return [];
  }

  const refs: Ref[] = [];

  sequencerConfigStates.forEach((config: SequencerConfigState) => {
    if (config.conditionExpression) {
      refs.push(...extractConditionExpressionRefs(config.conditionExpression));
    }
  });

  return [
    ...new Set(
      refs.filter((ref): ref is Ref => ref !== undefined && ref.namespace === Namespace.Setting)
    ),
  ];
};

export const extractReadingsRefsFromActiveSequencerConfig = (
  activeSequencer: SequencerConfigState | undefined
): Ref[] => {
  if (!activeSequencer) return [];

  return (
    activeSequencer?.displayMetrics
      ?.map((metric) => metric.ref)
      .filter((ref) => ref !== undefined && ref.namespace !== Namespace.Setting) ?? []
  );
};

export const extractSettingsRefsFromActiveSequencerConfig = (
  activeSequencer: SequencerConfigState | undefined
): Ref[] => {
  if (!activeSequencer) return [];

  return (
    activeSequencer?.displaySettings
      ?.map((setting) => setting.ref)
      .filter((ref) => ref !== undefined && ref.namespace === Namespace.Setting) ?? []
  );
};
